'use client';

import { useCallback, useMemo, useState } from 'react';
import { isBefore, startOfToday } from 'date-fns';

import { SearchFilterDateValues } from '@/app/(main)/(pages)/busca/[slug]/_types/search-filter-date.type';
import { fromIsoDate, toIsoDate } from '@/app/(main)/(pages)/busca/[slug]/_utils/filter-date.util';

interface UseDateRangeFilterParams {
  values: Readonly<SearchFilterDateValues>;
  onChange: (values: Partial<SearchFilterDateValues>) => void;
}

interface UseDateRangeFilterReturn {
  startDate: Date | undefined;
  endDate: Date | undefined;
  isRangeEnabled: boolean;
  isRangeToggleDisabled: boolean;
  isEndDateMissing: boolean;
  shouldShowEndDateError: boolean;
  minStartDate: Date;
  minEndDate: Date;
  setStartDate: (date?: Date) => void;
  setEndDate: (date?: Date) => void;
  toggleRange: (enabled: boolean) => void;
  visitEndDate: () => void;
}

export function useDateRangeFilter({ values, onChange }: UseDateRangeFilterParams): UseDateRangeFilterReturn {
  const [isRangeRequested, setIsRangeRequested] = useState(false);
  const [hasVisitedEndDate, setHasVisitedEndDate] = useState(false);

  const startDate = useMemo(() => fromIsoDate(values.minDate), [values.minDate]);
  const endDate = useMemo(() => fromIsoDate(values.maxDate), [values.maxDate]);

  const minStartDate = useMemo(() => startOfToday(), []);
  const minEndDate = startDate ?? minStartDate;
  const isRangeEnabled = Boolean(startDate) && (isRangeRequested || Boolean(endDate));
  const isEndDateMissing = isRangeEnabled && !endDate;

  const setStartDate = useCallback(
    (date?: Date) => {
      if (!date) {
        setIsRangeRequested(false);
        setHasVisitedEndDate(false);
        onChange({ minDate: undefined, maxDate: undefined });
        return;
      }

      const keepsEndDate = Boolean(endDate) && !isBefore(endDate!, date);

      if (endDate && !keepsEndDate) {
        setIsRangeRequested(true);
        setHasVisitedEndDate(true);
      }

      onChange({ minDate: toIsoDate(date), ...(keepsEndDate ? {} : { maxDate: undefined }) });
    },
    [endDate, onChange],
  );

  const setEndDate = useCallback(
    (date?: Date) => {
      if (!date) {
        setIsRangeRequested(true);
      }

      onChange({ maxDate: date ? toIsoDate(date) : undefined });
    },
    [onChange],
  );

  const toggleRange = useCallback(
    (enabled: boolean) => {
      setIsRangeRequested(enabled);
      setHasVisitedEndDate(false);

      if (!enabled) {
        onChange({ maxDate: undefined });
      }
    },
    [onChange],
  );

  const visitEndDate = useCallback(() => setHasVisitedEndDate(true), []);

  return {
    startDate,
    endDate,
    isRangeEnabled,
    isRangeToggleDisabled: !startDate,
    isEndDateMissing,
    shouldShowEndDateError: isEndDateMissing && hasVisitedEndDate,
    minStartDate,
    minEndDate,
    setStartDate,
    setEndDate,
    toggleRange,
    visitEndDate,
  };
}
